import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { readStaleSessionCache, writeSessionCache } from "@/lib/sessionCache";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Users, FileText, CreditCard, AlertTriangle, CheckCircle, XCircle, Clock, StickyNote, Plus, Trash2, Check, Bell, BellRing, Volume2, Package, FileWarning, PhoneCall, PhoneOutgoing, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useYear } from "@/contexts/YearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// Componente skeleton per righe in caricamento
function SkeletonRows({ count = 3, color = "slate" }) {
  const colorMap = {
    slate: "bg-slate-100",
    orange: "bg-orange-100",
    red: "bg-red-100",
    amber: "bg-amber-100",
    blue: "bg-blue-100",
  };
  const bg = colorMap[color] || colorMap.slate;
  return (
    <div className="space-y-3" data-testid="skeleton-rows">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`flex items-center gap-3 p-3 rounded-lg animate-pulse ${bg}`}>
          <div className="w-10 h-10 rounded-full bg-white/60" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-white/80 rounded w-3/4" />
            <div className="h-2.5 bg-white/60 rounded w-1/2" />
          </div>
          <div className="w-16 h-6 bg-white/60 rounded" />
        </div>
      ))}
      <div className="text-center text-xs text-slate-500 italic pt-1">Caricamento dati in background...</div>
    </div>
  );
}

// Bottone toggle collapse per le sezioni della Dashboard
function CollapseToggle({ collapsed, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ml-auto p-1.5 rounded-md hover:bg-black/5 transition-colors ${className}`}
      aria-label={collapsed ? "Espandi sezione" : "Comprimi sezione"}
      data-testid="collapse-toggle"
    >
      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
    </button>
  );
}


export default function Dashboard({ user, logout }) {
  const { selectedYear } = useYear();
  const navigate = useNavigate();
  // ==================== SESSION CACHE (persiste tra navigazioni tab) ====================
  // Al mount tenta hydration da sessionStorage: se dati <5 min, la Dashboard appare istantanea
  // Poi comunque rilancia fetchData in background per aggiornamento silenzioso.
  const currentYearRef = useRef(null);
  const _dashCached = readStaleSessionCache(`dashboard_state_${selectedYear}`);
  const saveToCache = (data) => writeSessionCache(`dashboard_state_${currentYearRef.current || selectedYear}`, data);
  
  const [interviewStats, setInterviewStats] = useState(_dashCached?.interviewStats || { totale: 0, vinti: 0, persi: 0, spariti: 0 });
  const [urgentData, setUrgentData] = useState(_dashCached?.urgentData || { warning: [], critical: [] });
  const [paymentStats, setPaymentStats] = useState(_dashCached?.paymentStats || { totale_clienti: 0, totale_contratti: 0, totale_incassato: 0, totale_da_incassare: 0 });
  const [pendingPayments, setPendingPayments] = useState(_dashCached?.pendingPayments || []);
  const [boardNotes, setBoardNotes] = useState(_dashCached?.boardNotes || []);
  const [newNote, setNewNote] = useState("");
  const [clientiDaPosare, setClientiDaPosare] = useState(_dashCached?.clientiDaPosare || []);
  const [datePosaScadute, setDatePosaScadute] = useState(_dashCached?.datePosaScadute || []);
  const [ordiniScaduti, setOrdiniScaduti] = useState(_dashCached?.ordiniScaduti || []);
  const [anomalieContrattuali, setAnomalieContrattuali] = useState(_dashCached?.anomalieContrattuali || []);
  const [followUpClienti, setFollowUpClienti] = useState(_dashCached?.followUpClienti || []);
  const [addingToFollowUp, setAddingToFollowUp] = useState(null);
  const [markingAsCalled, setMarkingAsCalled] = useState(null);
  // Se ho dati in cache, parto già "loaded" (invisibile revalidation dopo)
  const hasHydratedData = !!_dashCached;
  const [loading, setLoading] = useState(!hasHydratedData);
  const [wave2Loading, setWave2Loading] = useState(!hasHydratedData);
  
  // Collapse state per ogni sezione (persistito in localStorage per utente)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboard_collapsed_sections");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("dashboard_collapsed_sections", JSON.stringify(next)); } catch { /* localStorage disabled */ }
      return next;
    });
  };
  
  // Stati per promemoria
  const [promemoria, setPromemoria] = useState({ data: "", ora: "" });
  const [activeReminders, setActiveReminders] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState("default");
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const fetchData = async () => {
    currentYearRef.current = selectedYear;
    // ===== WAVE 1: dati core rapidi (visualizzati subito) =====
    // stats interviste + payments + urgent → cache backend, veloci
    let wave1Data = null;
    try {
      const [statsRes, urgentRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/interviews/stats?year=${selectedYear}`, { withCredentials: true }),
        axios.get(`${API}/interviews/urgent?year=${selectedYear}`, { withCredentials: true }),
        axios.get(`${API}/payments/stats?year=${selectedYear}`, { withCredentials: true }),
      ]);
      wave1Data = { interviewStats: statsRes.data, urgentData: urgentRes.data, paymentStats: paymentsRes.data };
      setInterviewStats(statsRes.data);
      setUrgentData(urgentRes.data);
      setPaymentStats(paymentsRes.data);
      // Salvataggio parziale immediato: se user navica prima di Wave 2, la cache
      // avrà comunque i dati principali (KPI, urgent, payments).
      saveToCache({
        ...wave1Data,
        pendingPayments: pendingPayments,
        boardNotes: boardNotes,
        clientiDaPosare: clientiDaPosare,
        datePosaScadute: datePosaScadute,
        ordiniScaduti: ordiniScaduti,
        anomalieContrattuali: anomalieContrattuali,
        followUpClienti: followUpClienti,
      });
      // Rendering dei counters immediatamente
      setLoading(false);
    } catch (e) {
      console.error("Wave 1 dashboard error:", e);
      setLoading(false);
    }
    
    // ===== WAVE 2: dati secondari (background, non blocca UI) =====
    // Non resetta a true se avevamo dati in cache: revalidation silenziosa
    if (!hasHydratedData) setWave2Loading(true);
    try {
      const [pendingRes, notesRes, clientiDaPosareRes, datePosaScaduteRes, cronoOrdiniRes, ordiniRes, interviewsRes] = await Promise.all([
        axios.get(`${API}/payments/pending?year=${selectedYear}`, { withCredentials: true }),
        axios.get(`${API}/board-notes?year=${selectedYear}`, { withCredentials: true }),
        axios.get(`${API}/commesse-pose/clienti-da-posare?year=${selectedYear}`, { withCredentials: true }),
        axios.get(`${API}/commesse-pose/date-posa-scadute?year=${selectedYear}`, { withCredentials: true }),
        axios.get(`${API}/crono-ordini`, { withCredentials: true }),
        axios.get(`${API}/ordini?year=${selectedYear}&light=true`, { withCredentials: true }),
        axios.get(`${API}/interviews?year=${selectedYear}`, { withCredentials: true })
      ]);
      setPendingPayments(pendingRes.data);
      setBoardNotes(notesRes.data);
      setClientiDaPosare(clientiDaPosareRes.data);
      setDatePosaScadute(datePosaScaduteRes.data);
      setOrdiniScaduti(cronoOrdiniRes.data?.scaduti || []);
      
      // Controlla e invia notifiche Follow Up (background)
      axios.post(`${API}/follow-up/check-notifications`, {}, { withCredentials: true })
        .then(res => {
          if (res.data.notifiche_inviate > 0) {
            console.log(`Inviate ${res.data.notifiche_inviate} notifiche Follow Up`);
          }
        })
        .catch(err => console.log("Follow Up check error:", err));
      
      // Calcola clienti da Follow Up (consegnati senza esito da oltre 3 giorni)
      const interviews = interviewsRes.data;
      const oggi = new Date();
      const treGiorniFa = new Date(oggi.getTime() - 3 * 24 * 60 * 60 * 1000);
      
      const followUp = interviews.filter(interview => {
        // Deve essere in stato "Consegnato" e senza esito
        if (interview.stato !== "Consegnato") return false;
        if (interview.esito && interview.esito !== "" && interview.esito !== "in_attesa") return false;
        
        // Escludi se già segnato come "chiamato"
        if (interview.follow_up_chiamato) return false;
        
        // Controlla data_consegnato o data_stato come fallback
        const dataRiferimento = interview.data_consegnato || interview.data_stato;
        if (!dataRiferimento) return false;
        
        try {
          const dataConsegnato = new Date(dataRiferimento);
          return dataConsegnato <= treGiorniFa;
        } catch {
          return false;
        }
      }).map(interview => {
        const dataRiferimento = interview.data_consegnato || interview.data_stato;
        const dataConsegnato = new Date(dataRiferimento);
        const giorniPassati = Math.floor((oggi - dataConsegnato) / (1000 * 60 * 60 * 24));
        
        return {
          id: interview.id,
          nome: `${interview.nome || ''} ${interview.cognome || ''}`.trim(),
          telefono: interview.telefono || "",
          email: interview.email || "",
          citta: interview.citta || "",
          giorniPassati
        };
      });
      
      setFollowUpClienti(followUp);
      
      // Calcola anomalie contrattuali (documenti in attesa di firma)
      const ordini = ordiniRes.data;
      const tipiDocumento = ["post_esecutivo", "condizioni_contrattuali", "norme_uni"];
      
      const anomalie = ordini.filter(ordine => {
        const docs = ordine.documenti || [];
        const nonNecessari = ordine.documenti_non_necessari || {};
        const docusignFlags = ordine.docusign_flags || {};
        const docusignData = ordine.docusign_data || {};
        
        // Documenti autografi in attesa
        const hasAutografiInAttesa = docs.some(d => 
          tipiDocumento.includes(d.tipo) && 
          !nonNecessari[d.tipo] &&
          !docusignFlags[d.tipo] &&
          d.file_path && 
          !d.ricevuto_firmato
        );
        
        // Documenti Docusign inviati ma non ricevuti
        const hasDocusignInAttesa = ["condizioni_contrattuali", "norme_uni"].some(tipo =>
          docusignFlags[tipo] && !docusignData[tipo]?.ricevuto
        );
        
        // Documenti mancanti (non caricati e non flaggati come non necessari)
        const tipiDaControllare = tipiDocumento.filter(tipo => !nonNecessari[tipo] && !docusignFlags[tipo]);
        const tipiCaricati = docs.filter(d => tipiDaControllare.includes(d.tipo) && d.file_path).map(d => d.tipo);
        const hasDocumentiMancanti = tipiCaricati.length < tipiDaControllare.length;
        
        return hasAutografiInAttesa || hasDocusignInAttesa || hasDocumentiMancanti;
      }).map(ordine => {
        const docs = ordine.documenti || [];
        const nonNecessari = ordine.documenti_non_necessari || {};
        const docusignFlags = ordine.docusign_flags || {};
        const docusignData = ordine.docusign_data || {};
        
        let problemi = [];
        
        // Conta documenti autografi in attesa
        const autografiInAttesa = docs.filter(d => 
          tipiDocumento.includes(d.tipo) && 
          !nonNecessari[d.tipo] && 
          !docusignFlags[d.tipo] &&
          d.file_path && 
          !d.ricevuto_firmato
        ).length;
        if (autografiInAttesa > 0) {
          problemi.push(`${autografiInAttesa} doc. in attesa firma`);
        }
        
        // Conta documenti Docusign in attesa
        const docusignInAttesa = ["condizioni_contrattuali", "norme_uni"].filter(tipo =>
          docusignFlags[tipo] && !docusignData[tipo]?.ricevuto
        ).length;
        if (docusignInAttesa > 0) {
          problemi.push(`${docusignInAttesa} Docusign in attesa`);
        }
        
        // Conta documenti mancanti
        const tipiDaControllare = tipiDocumento.filter(tipo => !nonNecessari[tipo] && !docusignFlags[tipo]);
        const tipiCaricati = docs.filter(d => tipiDaControllare.includes(d.tipo) && d.file_path).map(d => d.tipo);
        const mancanti = tipiDaControllare.length - tipiCaricati.length;
        if (mancanti > 0) {
          problemi.push(`${mancanti} doc. da caricare`);
        }
        
        return {
          id: ordine.id,
          nome: `${ordine.nome || ''} ${ordine.cognome || ''}`.trim(),
          problemi: problemi.join(", ")
        };
      });
      
      setAnomalieContrattuali(anomalie);
      
      // ==================== Salva snapshot in sessionStorage ====================
      // Al prossimo mount della Dashboard (utente naviga altrove e torna),
      // questi dati vengono usati per rendering istantaneo. La revalidation avviene in bg.
      if (wave1Data) {
        saveToCache({
          ...wave1Data,
          pendingPayments: pendingRes.data,
          boardNotes: notesRes.data,
          clientiDaPosare: clientiDaPosareRes.data,
          datePosaScadute: datePosaScaduteRes.data,
          ordiniScaduti: cronoOrdiniRes.data?.scaduti || [],
          anomalieContrattuali: anomalie,
          followUpClienti: followUp,
        });
      }
    } catch (e) {
      console.error("Wave 2 dashboard error:", e);
    } finally {
      setWave2Loading(false);
    }
  };

  const handleAddToFollowUp = async (cliente) => {
    setAddingToFollowUp(cliente.id);
    try {
      // Invia al webhook/Google Sheets
      await axios.post(`${API}/follow-up/add`, {
        id: cliente.id,
        nome: cliente.nome,
        telefono: cliente.telefono,
        email: cliente.email,
        citta: cliente.citta,
        giorni_passati: cliente.giorniPassati,
        data_aggiunta: new Date().toISOString()
      }, { withCredentials: true });
      
      // Rimuovi dalla lista locale
      setFollowUpClienti(prev => prev.filter(c => c.id !== cliente.id));
      toast.success(`${cliente.nome} aggiunto al Follow Up`);
    } catch (e) {
      console.error("Error adding to follow up:", e);
      toast.error("Errore nell'aggiunta al Follow Up");
    } finally {
      setAddingToFollowUp(null);
    }
  };

  const handleOpenInterview = (interviewId) => {
    // Naviga alla pagina interviste con l'ID dell'intervista da aprire
    navigate(`/interviste?edit=${interviewId}`);
  };

  const handleMarkAsCalled = async (cliente) => {
    setMarkingAsCalled(cliente.id);
    try {
      // Chiama l'endpoint per segnare come chiamato
      await axios.post(`${API}/follow-up/mark-called`, {
        interview_id: cliente.id
      }, { withCredentials: true });
      
      // Rimuovi dalla lista locale
      setFollowUpClienti(prev => prev.filter(c => c.id !== cliente.id));
      
      // Mostra messaggio con promemoria
      toast.success(
        <div>
          <p className="font-semibold">✅ {cliente.nome} segnato come chiamato</p>
          <p className="text-sm mt-1">Ricorda di aggiornare:</p>
          <ul className="text-sm list-disc ml-4">
            <li>Note nella scheda intervista</li>
            <li>Esito dell&apos;intervista</li>
          </ul>
        </div>,
        { duration: 8000 }
      );
      
      // Apri automaticamente la scheda intervista per modificarla
      setTimeout(() => {
        if (window.confirm(`Vuoi aprire la scheda di ${cliente.nome} per aggiornare note ed esito?`)) {
          navigate(`/interviste?edit=${cliente.id}`);
        }
      }, 500);
      
    } catch (e) {
      console.error("Error marking as called:", e);
      toast.error("Errore nel segnare come chiamato");
    } finally {
      setMarkingAsCalled(null);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Richiedi permesso notifiche
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === "default") {
        Notification.requestPermission().then(perm => {
          setNotificationPermission(perm);
        });
      }
    }
    
    // Cleanup
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [selectedYear]); // Ricarica quando cambia l'anno

  // Controlla promemoria ogni 30 secondi
  const checkReminders = useCallback(() => {
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().slice(0, 5);
    
    boardNotes.forEach(note => {
      if (note.promemoria_data && note.promemoria_ora && !note.promemoria_completato && !note.elaborata) {
        // Controlla se il promemoria è scaduto
        if (note.promemoria_data <= currentDate) {
          if (note.promemoria_data < currentDate || note.promemoria_ora <= currentTime) {
            // Promemoria scaduto!
            if (!activeReminders.find(r => r.id === note.id)) {
              triggerReminder(note);
            }
          }
        }
      }
    });
  }, [boardNotes, activeReminders]);

  useEffect(() => {
    // Avvia controllo periodico
    checkIntervalRef.current = setInterval(checkReminders, 30000); // ogni 30 sec
    checkReminders(); // check immediato
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkReminders]);

  // Funzione per attivare un promemoria
  const triggerReminder = (note) => {
    setActiveReminders(prev => [...prev, note]);
    
    // Suona
    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.play().catch(e => console.log("Audio play blocked:", e));
    }
    
    // Notifica browser
    if (notificationPermission === "granted") {
      const notification = new Notification("⏰ Promemoria CRM", {
        body: note.testo,
        icon: "/favicon.ico",
        requireInteraction: true,
        tag: note.id
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
    
    // Toast persistente
    toast.warning(`⏰ PROMEMORIA: ${note.testo}`, {
      duration: Infinity,
      id: `reminder-${note.id}`
    });
  };

  // Completa un promemoria
  const handleCompleteReminder = async (noteId) => {
    try {
      await axios.put(`${API}/board-notes/${noteId}`, { promemoria_completato: true }, { withCredentials: true });
      
      // Rimuovi dai promemoria attivi
      setActiveReminders(prev => prev.filter(r => r.id !== noteId));
      
      // Ferma audio se non ci sono altri promemoria
      if (activeReminders.length <= 1 && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // Chiudi toast
      toast.dismiss(`reminder-${noteId}`);
      
      fetchData();
      toast.success("Promemoria completato");
    } catch (e) {
      toast.error("Errore nel completamento");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const noteData = { 
        testo: newNote,
        promemoria_data: promemoria.data || null,
        promemoria_ora: promemoria.ora || null
      };
      await axios.post(`${API}/board-notes`, noteData, { withCredentials: true });
      setNewNote("");
      setPromemoria({ data: "", ora: "" });
      fetchData();
      toast.success(promemoria.data ? "Nota con promemoria aggiunta" : "Nota aggiunta");
    } catch (e) {
      console.error("Error adding note:", e);
      toast.error("Errore nell'aggiunta");
    }
  };

  const handleToggleElaborata = async (noteId, currentValue) => {
    try {
      await axios.put(`${API}/board-notes/${noteId}`, { elaborata: !currentValue }, { withCredentials: true });
      fetchData();
      toast.success(currentValue ? "Nota riaperta" : "Nota segnata come elaborata");
    } catch (e) {
      console.error("Error updating note:", e);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    try {
      await axios.delete(`${API}/board-notes/${noteId}`, { withCredentials: true });
      fetchData();
      toast.success("Nota eliminata");
    } catch (e) {
      console.error("Error deleting note:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <PageLayout user={user} logout={logout} testId="dashboard-page" className="overflow-auto">
        <div className="p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-600 mt-1">Panoramica dello showroom</p>
          </div>

          {/* Struttura sempre visibile: KPI counters mostrano 0 finché Wave 1 non è pronta */}
          <>
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card className={`stat-card ${loading ? "animate-pulse" : ""}`} data-testid="stat-totale">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Clienti Gestiti</p>
                        <p className="text-3xl font-bold text-slate-900 mt-1">{interviewStats.totale}</p>
                      </div>
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`stat-card ${loading ? "animate-pulse" : ""}`} data-testid="stat-vinti">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Vinti</p>
                        <p className="text-3xl font-bold text-emerald-600 mt-1">{interviewStats.vinti}</p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`stat-card ${loading ? "animate-pulse" : ""}`} data-testid="stat-persi">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Persi</p>
                        <p className="text-3xl font-bold text-rose-600 mt-1">{interviewStats.persi}</p>
                      </div>
                      <div className="w-12 h-12 bg-rose-50 rounded-lg flex items-center justify-center">
                        <XCircle className="w-6 h-6 text-rose-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`stat-card ${loading ? "animate-pulse" : ""}`} data-testid="stat-spariti">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Spariti</p>
                        <p className="text-3xl font-bold text-amber-600 mt-1">{interviewStats.spariti}</p>
                      </div>
                      <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Urgent Interviews & Payment Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Urgent Interviews */}
                <Card data-testid="urgent-interviews-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Clienti in Attesa
                      <CollapseToggle collapsed={collapsedSections["urgent"]} onClick={() => toggleSection("urgent")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["urgent"] && (
                  <CardContent>
                    {loading ? (
                      <SkeletonRows count={2} color="amber" />
                    ) : (urgentData.warning?.length === 0 && urgentData.critical?.length === 0) ? (
                      <p className="text-slate-500 text-sm">Nessun cliente urgente</p>
                    ) : (
                      <div className="space-y-3">
                        {/* Critical - oltre 5 giorni */}
                        {urgentData.critical?.slice(0, 3).map((interview) => (
                          <Link
                            key={interview.id}
                            to="/interviews"
                            className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
                              <div>
                                <p className="font-medium text-red-900">
                                  {interview.nome} {interview.cognome}
                                </p>
                                <p className="text-xs text-red-600">
                                  {interview.stato} • {interview.days_since_interview} giorni - URGENTE
                                </p>
                              </div>
                            </div>
                            <span className="font-mono text-xs text-red-400">
                              {interview.codice}
                            </span>
                          </Link>
                        ))}
                        {/* Warning - 3-5 giorni */}
                        {urgentData.warning?.slice(0, 3).map((interview) => (
                          <Link
                            key={interview.id}
                            to="/interviews"
                            className="flex items-center justify-between p-3 bg-rose-50 rounded-lg hover:bg-rose-100 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 bg-rose-500 rounded-full animate-pulse" />
                              <div>
                                <p className="font-medium text-slate-900">
                                  {interview.nome} {interview.cognome}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {interview.stato} • {interview.days_since_interview} giorni
                                </p>
                              </div>
                            </div>
                            <span className="font-mono text-xs text-slate-400">
                              {interview.codice}
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </CardContent>
                  )}
                </Card>

                {/* Payment Summary */}
                <Card data-testid="payment-summary-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CreditCard className="w-5 h-5 text-slate-600" />
                      Riepilogo Pagamenti
                      <CollapseToggle collapsed={collapsedSections["payments"]} onClick={() => toggleSection("payments")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["payments"] && (
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Totale Clienti</span>
                        <span className="font-semibold text-slate-900">{paymentStats.totale_clienti}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Totale Contratti</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(paymentStats.totale_contratti)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Totale Incassato</span>
                        <span className="font-semibold text-emerald-600">{formatCurrency(paymentStats.totale_incassato)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Da Incassare</span>
                        <span className="font-semibold text-rose-600">{formatCurrency(paymentStats.totale_da_incassare)}</span>
                      </div>
                      
                      {/* Clienti con pagamenti in sospeso */}
                      {pendingPayments.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-slate-700 mb-2">Pagamenti in Sospeso:</p>
                          <div className="space-y-2">
                            {pendingPayments.slice(0, 5).map((client) => (
                              <Link
                                key={client.id}
                                to="/payments"
                                className="flex items-center justify-between p-2 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors border border-violet-200"
                              >
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4 text-violet-600 animate-pulse" />
                                  <span className="font-medium text-violet-900 text-sm">
                                    {client.nome} {client.cognome}
                                  </span>
                                </div>
                                <span className="text-xs text-violet-600">
                                  Tranche {client.tranche_num} • {formatCurrency(client.importo_da_pagare)}
                                </span>
                              </Link>
                            ))}
                            {pendingPayments.length > 5 && (
                              <Link to="/payments" className="text-sm text-violet-600 hover:underline">
                                + altri {pendingPayments.length - 5} clienti
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  )}
                </Card>
              </div>

              {/* Bacheca */}
              <Card className="mt-6" data-testid="bacheca-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <StickyNote className="w-5 h-5 text-blue-500" />
                    Bacheca
                    {activeReminders.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full animate-pulse">
                        {activeReminders.length} promemoria attivi
                      </span>
                    )}
                    <CollapseToggle collapsed={collapsedSections["bacheca"]} onClick={() => toggleSection("bacheca")} />
                  </CardTitle>
                </CardHeader>
                {!collapsedSections["bacheca"] && (
                <CardContent>
                  {/* Audio per suoneria */}
                  <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" />
                  
                  {/* Promemoria attivi */}
                  {activeReminders.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {activeReminders.map(reminder => (
                        <div key={reminder.id} className="flex items-center justify-between p-3 bg-red-100 border-2 border-red-500 rounded-lg animate-pulse">
                          <div className="flex items-center gap-3">
                            <BellRing className="w-6 h-6 text-red-600 animate-bounce" />
                            <div>
                              <p className="font-bold text-red-800">⏰ PROMEMORIA!</p>
                              <p className="text-sm text-red-700">{reminder.testo}</p>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleCompleteReminder(reminder.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Completato
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Form per nuova nota */}
                  <div className="space-y-3 mb-4 p-3 bg-slate-50 rounded-lg border">
                    <Textarea
                      placeholder="Scrivi una nota, appunto o messaggio..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      className="w-full bg-white"
                      data-testid="new-note-input"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-orange-500" />
                        <span className="text-sm text-slate-600">Promemoria:</span>
                        <Input
                          type="date"
                          value={promemoria.data}
                          onChange={(e) => setPromemoria({ ...promemoria, data: e.target.value })}
                          className="w-40"
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <Input
                          type="time"
                          value={promemoria.ora}
                          onChange={(e) => setPromemoria({ ...promemoria, ora: e.target.value })}
                          className="w-32"
                        />
                      </div>
                      <div className="flex-1 flex justify-end">
                        <Button 
                          onClick={handleAddNote}
                          className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                          data-testid="add-note-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Aggiungi Nota
                        </Button>
                      </div>
                    </div>
                    {notificationPermission !== "granted" && (
                      <p className="text-xs text-orange-600 flex items-center gap-1">
                        <Volume2 className="w-3 h-3" />
                        Attiva le notifiche browser per ricevere avvisi sonori
                      </p>
                    )}
                  </div>
                  
                  {/* Lista note */}
                  {boardNotes.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">Nessuna nota in bacheca</p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {boardNotes.map((note) => (
                        <div
                          key={note.id}
                          className={`flex items-start justify-between p-3 rounded-lg border ${
                            note.elaborata 
                              ? "bg-slate-50 border-slate-200 opacity-60" 
                              : note.promemoria_data && !note.promemoria_completato
                                ? "bg-orange-50 border-orange-300"
                                : "bg-blue-50 border-blue-200"
                          }`}
                          data-testid={`note-${note.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-xs text-blue-600 font-medium">
                                {note.autore_nome || "Autore sconosciuto"}
                              </p>
                              {note.promemoria_data && !note.promemoria_completato && (
                                <span className="flex items-center gap-1 text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">
                                  <Bell className="w-3 h-3" />
                                  {note.promemoria_data} {note.promemoria_ora}
                                </span>
                              )}
                              {note.promemoria_completato && (
                                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                                  ✓ Promemoria completato
                                </span>
                              )}
                            </div>
                            <p className={`text-sm ${note.elaborata ? "line-through text-slate-500" : "text-slate-700"}`}>
                              {note.testo}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleElaborata(note.id, note.elaborata)}
                              className={`p-1 h-auto ${note.elaborata ? "text-slate-400" : "text-emerald-600 hover:text-emerald-700"}`}
                              title={note.elaborata ? "Riapri" : "Segna come elaborata"}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-rose-600 hover:text-rose-700 p-1 h-auto"
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                )}
              </Card>

              {/* Clienti da Posare/Completare */}
              {(wave2Loading || clientiDaPosare.length > 0) && (
                <Card className="mb-6" data-testid="clienti-da-posare-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Package className="w-5 h-5 text-orange-500" />
                      Clienti da Posare/Completare
                      <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                        {wave2Loading ? "..." : clientiDaPosare.length}
                      </span>
                      <CollapseToggle collapsed={collapsedSections["clienti-posare"]} onClick={() => toggleSection("clienti-posare")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["clienti-posare"] && (
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-4">
                      Clienti con merce arrivata in deposito ma non ancora posata
                    </p>
                    {wave2Loading ? (
                      <SkeletonRows count={3} color="orange" />
                    ) : (
                    <div className="space-y-3">
                      {clientiDaPosare.slice(0, 10).map((cliente) => (
                        <Link
                          key={cliente.commessa_id}
                          to={`/commesse-pose/${cliente.commessa_id}`}
                          className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors border border-orange-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                              <Package className="w-5 h-5 text-orange-700" />
                            </div>
                            <div>
                              <p className="font-medium text-orange-900">
                                {cliente.cliente_nome} {cliente.cliente_cognome}
                              </p>
                              <p className="text-xs text-orange-600">
                                {cliente.articoli_da_posare} articoli da posare su {cliente.totale_articoli} totali
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-orange-500 block">
                              {cliente.status === "programmata" ? "Programmata" : cliente.status === "in_deposito" ? "In deposito" : cliente.status}
                            </span>
                            {cliente.data_posa && (
                              <span className="text-xs text-slate-500">
                                Posa: {new Date(cliente.data_posa).toLocaleDateString('it-IT')}
                              </span>
                            )}
                          </div>
                        </Link>
                      ))}
                      {clientiDaPosare.length > 10 && (
                        <Link to="/commesse-pose" className="text-sm text-orange-600 hover:underline block text-center">
                          + altri {clientiDaPosare.length - 10} clienti
                        </Link>
                      )}
                    </div>
                    )}
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Date Posa Scadute */}
              {(wave2Loading || datePosaScadute.length > 0) && (
                <Card className="mb-6 border-red-200" data-testid="date-posa-scadute-card">
                  <CardHeader className="bg-red-50">
                    <CardTitle className="flex items-center gap-2 text-lg text-red-800">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Date Posa Scadute
                      <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {wave2Loading ? "..." : datePosaScadute.length}
                      </span>
                      <CollapseToggle collapsed={collapsedSections["date-scadute"]} onClick={() => toggleSection("date-scadute")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["date-scadute"] && (
                  <CardContent className="pt-4">
                    <p className="text-sm text-red-600 mb-4">
                      ⚠️ Commesse con data posa passata - Aggiorna lo stato!
                    </p>
                    {wave2Loading ? (
                      <SkeletonRows count={3} color="red" />
                    ) : (
                    <div className="space-y-3">
                      {datePosaScadute.slice(0, 10).map((cliente) => (
                        <Link
                          key={cliente.commessa_id}
                          to={`/commesse-pose/${cliente.commessa_id}`}
                          className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-300"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                              <AlertTriangle className="w-5 h-5 text-red-700" />
                            </div>
                            <div>
                              <p className="font-medium text-red-900">
                                {cliente.cliente_nome} {cliente.cliente_cognome}
                              </p>
                              <p className="text-xs text-red-600">
                                {cliente.indirizzo || "Indirizzo non specificato"}
                              </p>
                              {cliente.posatore_nome && (
                                <p className="text-xs text-slate-500">
                                  Posatore: {cliente.posatore_nome}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-semibold text-red-700 block">
                              {cliente.giorni_ritardo} {cliente.giorni_ritardo === 1 ? "giorno" : "giorni"} fa
                            </span>
                            <span className="text-xs text-slate-500">
                              Posa prevista: {new Date(cliente.data_posa).toLocaleDateString('it-IT')}
                            </span>
                            {cliente.ora_posa && (
                              <span className="text-xs text-slate-400 block">
                                ore {cliente.ora_posa}
                              </span>
                            )}
                          </div>
                        </Link>
                      ))}
                      {datePosaScadute.length > 10 && (
                        <Link to="/commesse-pose" className="text-sm text-red-600 hover:underline block text-center">
                          + altre {datePosaScadute.length - 10} commesse scadute
                        </Link>
                      )}
                    </div>
                    )}
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Ordini Scaduti (da Crono Ordini) */}
              {(wave2Loading || ordiniScaduti.length > 0) && (
                <Card className="mb-6 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="ordini-scaduti-card">
                  <CardHeader className="bg-amber-100/50">
                    <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
                      <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      Ordini Scaduti
                      <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full animate-pulse">
                        {wave2Loading ? "..." : ordiniScaduti.length}
                      </span>
                      <CollapseToggle collapsed={collapsedSections["ordini-scaduti"]} onClick={() => toggleSection("ordini-scaduti")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["ordini-scaduti"] && (
                  <CardContent className="pt-4">
                    <p className="text-sm text-amber-700 mb-4">
                      ⚠️ Ordini con data di INVIO prevista scaduta - Verifica lo stato!
                    </p>
                    {wave2Loading ? (
                      <SkeletonRows count={3} color="amber" />
                    ) : (
                    <div className="space-y-3">
                      {ordiniScaduti.slice(0, 10).map((ordine, idx) => (
                        <Link
                          key={`${ordine.commessa_id}-${ordine.articolo_index ?? idx}`}
                          to={`/commesse-pose/${ordine.commessa_id}`}
                          className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-amber-50 transition-colors border border-amber-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                              <Clock className="w-5 h-5 text-amber-700" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {ordine.cliente_nome} {ordine.cliente_cognome}
                              </p>
                              <p className="text-xs text-slate-500">
                                {ordine.articolo || "Articolo non specificato"}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-semibold text-amber-700 block">
                              {ordine.giorni_ritardo} {ordine.giorni_ritardo === 1 ? "giorno" : "giorni"} di ritardo
                            </span>
                            <span className="text-xs text-slate-500">
                              Previsto: {new Date(ordine.data_prevista).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        </Link>
                      ))}
                      {ordiniScaduti.length > 10 && (
                        <Link to="/crono-ordini" className="text-sm text-amber-600 hover:underline block text-center">
                          + altri {ordiniScaduti.length - 10} ordini scaduti
                        </Link>
                      )}
                    </div>
                    )}
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Anomalie Contrattuali */}
              {(wave2Loading || anomalieContrattuali.length > 0) && (
                <Card className="mb-6 border-2 border-red-400 bg-gradient-to-r from-red-50 to-rose-50 shadow-lg" data-testid="anomalie-contrattuali-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                      <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                        <FileWarning className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <span className="block">Anomalie Contrattuali</span>
                        <span className="text-xs font-normal text-red-500">Situazioni critiche da risolvere</span>
                      </div>
                      <span className="ml-auto px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full animate-pulse">
                        {wave2Loading ? "..." : anomalieContrattuali.length}
                      </span>
                      <CollapseToggle collapsed={collapsedSections["anomalie"]} onClick={() => toggleSection("anomalie")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["anomalie"] && (
                  <CardContent>
                    <p className="text-sm text-red-600 mb-4 font-medium">
                      I seguenti clienti hanno documenti mancanti o in attesa di firma
                    </p>
                    {wave2Loading ? (
                      <SkeletonRows count={3} color="red" />
                    ) : (
                    <>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {anomalieContrattuali.slice(0, 8).map((cliente) => (
                        <Link
                          key={cliente.id}
                          to="/ordini"
                          className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-red-100 transition-colors border border-red-300 shadow-sm"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-red-900">{cliente.nome}</p>
                              <p className="text-xs text-red-500">{cliente.problemi}</p>
                            </div>
                          </div>
                          <span className="text-xs text-red-400 font-medium">Vai a Contratti →</span>
                        </Link>
                      ))}
                    </div>
                    {anomalieContrattuali.length > 8 && (
                      <Link 
                        to="/ordini" 
                        className="mt-3 block text-center text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        + altri {anomalieContrattuali.length - 8} clienti con anomalie
                      </Link>
                    )}
                    </>
                    )}
                  </CardContent>
                  )}
                </Card>
              )}

              {/* Follow Up */}
              {(wave2Loading || followUpClienti.length > 0) && (
                <Card className="mb-6 border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-amber-50 shadow-lg" data-testid="follow-up-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg text-orange-700">
                      <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
                        <PhoneCall className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <span className="block">Follow Up</span>
                        <span className="text-xs font-normal text-orange-500">Clienti da richiamare</span>
                      </div>
                      <span className="ml-auto px-3 py-1 bg-orange-500 text-white text-sm font-bold rounded-full animate-pulse">
                        {wave2Loading ? "..." : followUpClienti.length}
                      </span>
                      <CollapseToggle collapsed={collapsedSections["follow-up"]} onClick={() => toggleSection("follow-up")} />
                    </CardTitle>
                  </CardHeader>
                  {!collapsedSections["follow-up"] && (
                  <CardContent>
                    <p className="text-sm text-orange-600 mb-4 font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Attenzione: chiamare per Follow Up i seguenti clienti (offerta consegnata da oltre 3 giorni senza esito)
                    </p>
                    {wave2Loading ? (
                      <SkeletonRows count={3} color="orange" />
                    ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {followUpClienti.map((cliente) => (
                        <div
                          key={cliente.id}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-300 shadow-sm"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <PhoneCall className="w-4 h-4 text-orange-600" />
                            </div>
                            <div>
                              <button
                                onClick={() => handleOpenInterview(cliente.id)}
                                className="font-semibold text-orange-900 hover:text-orange-600 hover:underline text-left bg-transparent"
                              >
                                {cliente.nome}
                              </button>
                              <p className="text-xs text-orange-500">
                                {cliente.citta} • {cliente.telefono} • {cliente.giorniPassati} giorni fa
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkAsCalled(cliente)}
                              disabled={markingAsCalled === cliente.id}
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                              data-testid={`mark-called-btn-${cliente.id}`}
                            >
                              {markingAsCalled === cliente.id ? (
                                "..."
                              ) : (
                                <>
                                  <PhoneOutgoing className="w-4 h-4 mr-1" />
                                  Chiamato
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleAddToFollowUp(cliente)}
                              disabled={addingToFollowUp === cliente.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {addingToFollowUp === cliente.id ? (
                                "..."
                              ) : (
                                <>
                                  <Plus className="w-4 h-4 mr-1" />
                                  Follow Up
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </CardContent>
                  )}
                </Card>
              )}



              {/* Quick Links */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <Link to="/interviews" data-testid="quick-link-interviews">
                  <Card className="stat-card hover:border-slate-300 cursor-pointer">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Interviste</h3>
                          <p className="text-sm text-slate-500">Gestisci le consulenze</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/offers" data-testid="quick-link-offers">
                  <Card className="stat-card hover:border-slate-300 cursor-pointer">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Offerte</h3>
                          <p className="text-sm text-slate-500">Invia offerte ai clienti</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/payments" data-testid="quick-link-payments">
                  <Card className="stat-card hover:border-slate-300 cursor-pointer">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Pagamenti</h3>
                          <p className="text-sm text-slate-500">Scadenzario pagamenti</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </>
        </div>
    </PageLayout>
  );
}
